// Free webinars (/free-webinars), as FreeWebinars.dc.html draws it: the webinars an owner or admin
// has added by hand and switched on (decision 30; the hub never reads Zoom, Adam, 5 Oct). Coming
// up has Register on Zoom, which opens the registration link typed in for it, Zoom's own page, in
// a new tab; Zoom emails the link to join, so the hub never shows "registered" (sign-off v11).
// Watch again is cards only (Adam, 23 Oct): each card, its button included, opens the replay's own
// page (WebinarReplay.jsx, /free-webinars/replay/<id>), where it plays. Nothing opens in place here.
// Replays stay until Adam takes them down. Someone held back from Free webinars gets "nothing here",
// as for any page that isn't theirs.
//
// Adam, 9 Oct: each card has his own thumbnail where it has 1 (else the date panel). Each card is
// #webinar-<id>, so a Library card for 1 coming up scrolls to it. The addresses a replay had before its
// own page, /free-webinars?replay=<id> and #webinar-<id> for a replay, go on to its page, replacing the
// address, so Back still leaves. Staff viewing as a member see the same thumbnails (Adam, 10 Oct).
import { useEffect } from 'react'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { Link, navigate, useQueryParam } from '../lib/router.jsx'
import { NotFound } from '../shell/Shell.jsx'
import { FreePage, PageHead, LoadFailed } from '../free/FreePage.jsx'
import { freeItems, webinarsProduct } from '../free/resources.js'
import { webinarPage, replayPath } from '../free/webinars.js'
import { useWebinarPage } from '../free/useWebinarPage.js'
import { Dot } from '../free/WebinarDot.jsx'
import { OutArrow, PlayIcon } from '../free/Icons.jsx'
import { useThumbnails } from '../free/thumbnails.js'
import { ThumbnailFrame } from '../free/Thumbnail.jsx'
import * as f from '../lib/format.js'

const LEDE = 'Live on Zoom with Adam. Register on Zoom and it emails you the link to join.'

export default function FreeWebinars() {
  // the address a Library replay card had before each replay had its own page
  const want = useQueryParam('replay')
  useEffect(() => { if (want) navigate(replayPath(want), { replace: true }) }, [want])
  if (want) return <div className="boot" />
  return <WebinarsPage />
}

function WebinarsPage() {
  const { me, lib, refreshLibrary } = useMember()
  const nowMs = useNow()
  const { page, load } = useWebinarPage(me.person.id)

  const ready = Boolean(lib.data) && page.status === 'ready'
  const view = ready ? webinarPage({ ...page.data, lib: lib.data, nowMs }) : null

  // #webinar-<id> naming a replay: its own page now, without drawing the list first (no flash)
  const goTo = view ? hashReplay(view) : null
  useEffect(() => { if (goTo) navigate(replayPath(goTo), { replace: true }) }, [goTo])

  // held back from Free webinars: their row isn't there to read
  if (lib.data && !webinarsProduct(lib.data)) return <NotFound />

  const failed = lib.status === 'error' || page.status === 'error'
  const hasResources = ready && freeItems(lib.data).length > 0
  const retry = () => {
    if (lib.status === 'error') refreshLibrary()
    if (page.status === 'error') load()
  }

  return (
    <FreePage title="Free webinars" variant="webinars">
      <PageHead heading="Free webinars" lede={LEDE} other={hasResources ? { to: '/free-resources', label: 'Free resources' } : null} />
      {failed && <LoadFailed onRetry={retry} />}
      {!failed && (!ready || goTo) && <div className="fp-loading" aria-busy="true" />}
      {view && !goTo && <Webinars view={view} />}
    </FreePage>
  )
}

// The replay the address's #webinar-<id> names, or null (1 still to come stays here, at its card)
function hashReplay(view) {
  const m = window.location.hash.match(/^#webinar-(.+)$/)
  if (!m) return null
  let id = null
  try { id = decodeURIComponent(m[1]) } catch { return null }
  return view.replays.some(r => r.id === id) ? id : null
}

function Webinars({ view }) {
  const { upcoming, replays } = view
  const thumbs = useThumbnails([...upcoming, ...replays])
  if (!upcoming.length && !replays.length) {
    return <p className="fp-empty">No webinars coming up just now. New ones show here when Adam adds them.</p>
  }
  return (
    <>
      {upcoming.length > 0 && (
        <section className="fw-section" aria-labelledby="fw-coming-up">
          <h2 id="fw-coming-up" className="fw-h2">Coming up</h2>
          {upcoming.map(w => <Upcoming key={w.id} w={w} thumbs={thumbs} />)}
        </section>
      )}
      {replays.length > 0 && (
        <section className="fw-section" aria-labelledby="fw-watch-again">
          <div className="fw-section-head">
            <h2 id="fw-watch-again" className="fw-h2">Watch again</h2>
            <span className="fw-aside">Replays stay here until Adam takes them down.</span>
          </div>
          {replays.map(r => <ReplayCard key={r.id} r={r} thumbs={thumbs} />)}
        </section>
      )}
    </>
  )
}

// The board's date block, as spans (display: flex in free.css), since it sits in the thumbnail's frame
function DateBlock({ block }) {
  return (
    <span className="fw-date">
      <span className="fw-date-edge">{block.dow}</span>
      <span className="fw-date-day">{block.day}</span>
      <span className="fw-date-edge">{block.mon}</span>
    </span>
  )
}

// The card's thumbnail, or the date panel with the board's date block
function CardThumb({ w, thumbs }) {
  return (
    <ThumbnailFrame thumb={thumbs.frame(w)} onError={e => thumbs.failed(w, e.currentTarget.src)}
      onLoad={e => thumbs.loaded(w, e.currentTarget.src)} className="fw-thumb">
      <DateBlock block={w.block} />
    </ThumbnailFrame>
  )
}

function Upcoming({ w, thumbs }) {
  return (
    <article className="fw-card" id={`webinar-${w.id}`}>
      <CardThumb w={w} thumbs={thumbs} />
      <div className="fw-text">
        <span className="fw-status"><Dot look={w.sellsLook} name={w.sellsName} />Live on Zoom with Adam</span>
        <h3 className="fw-title">{w.title}</h3>
        <span className="fw-time">{w.time}</span>
        {w.description && <span className="fw-desc">{w.description}</span>}
      </div>
      <div className="fw-act">
        {w.registrationUrl ? (
          <>
            <a className="fw-btn" href={w.registrationUrl} target="_blank" rel="noopener noreferrer">Register on Zoom<OutArrow /></a>
            <span className="fw-note">Opens Zoom's registration page in a new tab. Zoom emails you the link to join.</span>
          </>
        ) : (
          <span className="fw-note">Registration opens soon.</span>
        )}
      </div>
    </article>
  )
}

// A replay's card: the whole card is the link to its own page (as the Library's cards are), with
// Watch the replay drawn as its button, and where they've got to when they've started it
function ReplayCard({ r, thumbs }) {
  return (
    <Link to={replayPath(r.id)} className="fw-card fw-card--link" id={`webinar-${r.id}`}>
      <CardThumb w={r} thumbs={thumbs} />
      <div className="fw-text">
        <span className="fw-status"><Dot look={r.sellsLook} name={r.sellsName} />Replay</span>
        <h3 className="fw-title">{r.title}</h3>
        <span className="fw-time">{r.date}</span>
        {r.description && <span className="fw-desc">{r.description}</span>}
      </div>
      <div className="fw-act">
        <span className="fw-btn"><PlayIcon />Watch the replay</span>
        {r.savedSeconds > 0 && <span className="fw-note">Carry on from {f.clock(r.savedSeconds)}.</span>}
      </div>
    </Link>
  )
}
